import { useGetRefCarePlanFrequenciesEnumQuery } from '~/store/services/refCarePlanEnums';
import { useMemo } from 'react';
import { FrequencyUnitType } from '~/lib/types/carePlan/carePlans';
import { MultiSelectOption } from '@equip.health/ui';
import { datadogRum } from '@datadog/browser-rum';
import {
  NOT_ASSESSED_FREQUENCY_CODES,
  NOT_YET_ASSESSED_FREQUENCY_CODES,
} from '~/lib/constants/carePlan.constants';

export const getNotYetAssessedCode = (unitType: FrequencyUnitType): string => {
  if (unitType === 'DAYS') {
    return 'NOT_YET_ASSESSED_DAYS';
  }
  if (unitType === 'TIMES') {
    return 'NOT_YET_ASSESSED_TIMES';
  }
  // This should never happen with proper typing, but handle it defensively
  datadogRum.addError(
    new Error('Invalid frequency unit type provided to getNotYetAssessedCode'),
    { unitType },
  );
  return 'NOT_YET_ASSESSED_TIMES';
};

export const useCarePlanFrequencyOptions = ({
  frequencyCode,
  frequencyUnitType,
  isNoteContext = false,
}: {
  frequencyCode?: string;
  frequencyUnitType?: FrequencyUnitType;
  isNoteContext?: boolean;
}): {
  options: MultiSelectOption[];
  placeholder: string;
  isLoading: boolean;
} => {
  const { data: frequencies, isLoading } =
    useGetRefCarePlanFrequenciesEnumQuery();

  const options: MultiSelectOption[] = useMemo(() => {
    if (!frequencies || (!frequencyUnitType && !frequencyCode)) {
      return [];
    }
    let unitType = frequencyUnitType;
    if (!frequencyUnitType) {
      unitType = frequencies.find(
        (f) => f.code === frequencyCode,
      )?.unitTypeCode;
    }
    if (!unitType) {
      datadogRum.addError(
        new Error(
          'Highly unexpectedly, could not find frequency while deriving care plan frequency options',
        ),
        { frequencyCode },
      );
      return [];
    }

    return frequencies
      .filter((frequency) => frequency.unitTypeCode === unitType)
      .filter(
        (frequency) =>
          !NOT_YET_ASSESSED_FREQUENCY_CODES.includes(frequency.code),
      )
      .filter(
        (frequency) =>
          isNoteContext ||
          !NOT_ASSESSED_FREQUENCY_CODES.includes(frequency.code),
      )
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((frequency) => ({
        label: frequency.label,
        value: frequency.code,
      }));
  }, [frequencyUnitType, frequencyCode, frequencies, isNoteContext]);

  const placeholder: string = useMemo(() => {
    if (!frequencies) {
      return '';
    }

    // Missing frequencyCode is an expected state while the selection is uninitialized.
    if (!frequencyCode) {
      if (!frequencyUnitType) {
        return '';
      }

      const notYetAssessedCode = getNotYetAssessedCode(frequencyUnitType);
      const notYetAssessedFrequency = frequencies.find(
        (f) => f.code === notYetAssessedCode,
      );
      return notYetAssessedFrequency?.label || '';
    }

    if (NOT_YET_ASSESSED_FREQUENCY_CODES.includes(frequencyCode)) {
      const notYetAssessedFrequency = frequencies.find(
        (f) => f.code === frequencyCode,
      );
      return notYetAssessedFrequency?.label || '';
    }

    return '';
  }, [frequencies, frequencyCode, frequencyUnitType]);

  return { options, placeholder, isLoading };
};
